/**
 * Applications list — mockup-style table, real /api/v1/applications data only.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Select,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  AppstoreOutlined,
  CheckOutlined,
  DeleteOutlined,
  EditOutlined,
  FileTextOutlined,
  LineChartOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import IsEnabled from "../rolepermission";
import { accountService } from "../services/accountService";
import { applicationDiscoveryService } from "../services/applicationDiscoveryService";
import {
  TAB_KEY_ALL,
  TAB_KEY_DISCOVERY,
  TAB_KEY_ONBOARDED,
  TAB_KEY_UNTAGGED,
  appDisplayName,
  coverageDisplayLabel,
  coverageTagColor,
  filterApplicationsBySearch,
  filterApplicationsByTab,
  formatServiceKey,
  getApplicationOnboardingState,
  getDominantServiceType,
  getResourceSummary,
  parseTagsUnreadableError,
  serviceTypeTagColor,
} from "../utils/applicationDiscoveryHelpers";

const { Option } = Select;
const { Text, Title } = Typography;

const APP_TYPE_OPTIONS = [
  "Web App",
  "API Service",
  "Mobile App",
  "Microservice",
  "Batch Job",
  "Other",
];

const ENV_OPTIONS = ["PROD", "STAGING", "DEV", "QA"];

const CoverageProgress = ({
  icon: Icon,
  iconColor,
  onboarded,
  total,
  activeColor,
  isDark,
  emptyLabel,
  kind,
}) => {
  const kindClass =
    kind === "logs"
      ? "pipeline-discovery-progress--logs"
      : "pipeline-discovery-progress--metrics";

  if (total <= 0) {
    return (
      <div
        className={`pipeline-discovery-progress pipeline-discovery-progress--empty ${kindClass}`}
        data-theme={isDark ? "dark" : "light"}
      >
        <Icon style={{ color: iconColor, fontSize: 14, flexShrink: 0 }} />
        <span className="pipeline-discovery-progress-empty-label">{emptyLabel}</span>
      </div>
    );
  }

  const percent = Math.round((onboarded / total) * 100);
  const isComplete = onboarded === total;

  return (
    <div
      className={`pipeline-discovery-progress ${kindClass}`}
      data-theme={isDark ? "dark" : "light"}
      style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}
    >
      <Icon style={{ color: iconColor, fontSize: 14, flexShrink: 0 }} />
      <Progress
        percent={percent}
        size="small"
        strokeColor={isComplete ? "#10b981" : activeColor}
        trailColor={isDark ? "#30363d" : "#e5e7eb"}
        strokeWidth={8}
        format={() =>
          isComplete ? (
            <CheckOutlined style={{ color: "#10b981", fontSize: 12 }} />
          ) : (
            <span className="pipeline-discovery-progress-count">
              {onboarded}/{total}
            </span>
          )
        }
        style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
      />
    </div>
  );
};

const ApplicationsPage = () => {
  const { user, accounts, setAccounts } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isViewer = !IsEnabled("Accounts");

  const activeTab = searchParams.get("tab") || TAB_KEY_ALL;
  const setActiveTab = (key) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === TAB_KEY_ALL) next.delete("tab");
      else next.set("tab", key);
      return next;
    });
  };

  const searchQuery = searchParams.get("search") || "";
  const setSearchQuery = (val) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!val) next.delete("search");
      else next.set("search", val);
      return next;
    });
  };

  const accountIdFromUrl = searchParams.get("accountId") || "";

  const [accountsLoading, setAccountsLoading] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [applications, setApplications] = useState([]);
  const [untaggedResources, setUntaggedResources] = useState([]);
  const [untaggedLoading, setUntaggedLoading] = useState(false);
  const [untaggedError, setUntaggedError] = useState("");
  const [untaggedWarning, setUntaggedWarning] = useState("");
  const [selectedUntaggedKeys, setSelectedUntaggedKeys] = useState([]);
  const [assignUntaggedOpen, setAssignUntaggedOpen] = useState(false);
  const [assignUntaggedSlug, setAssignUntaggedSlug] = useState(null);
  const [assignUntaggedLoading, setAssignUntaggedLoading] = useState(false);
  const [addAttachKeys, setAddAttachKeys] = useState([]);
  const [addAttachLoading, setAddAttachLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [tagsError, setTagsError] = useState(null);
  const [discoverWarning, setDiscoverWarning] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addSaving, setAddSaving] = useState(false);
  const [addForm] = Form.useForm();
  const [editingApp, setEditingApp] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const completedAccounts = useMemo(
    () =>
      (accounts || []).filter(
        (a) =>
          String(a.status || "").toLowerCase() === "completed" ||
          String(a.onboarding_status || "").toLowerCase() === "completed"
      ),
    [accounts]
  );

  useEffect(() => {
    const fetchAccounts = async () => {
      if (!user?.client_id) return;
      if (accounts.length === 0 && !accountsLoading) {
        setAccountsLoading(true);
        try {
          const allAccounts = await accountService.getAccounts();
          const mapped = [];
          for (const clientId in allAccounts) {
            for (const accountId in allAccounts[clientId]) {
              mapped.push({
                ...allAccounts[clientId][accountId],
                accountId,
                clientId,
              });
            }
          }
          if (setAccounts) setAccounts(mapped);
        } catch (error) {
          console.error("Error fetching accounts:", error);
          message.error("Failed to load AWS accounts");
        } finally {
          setAccountsLoading(false);
        }
      }
    };
    fetchAccounts();
  }, [user?.client_id, accounts.length, accountsLoading, setAccounts]);

  useEffect(() => {
    if (!accountIdFromUrl || completedAccounts.length === 0) return;
    const match = completedAccounts.find(
      (a) => String(a.accountId || a.aws_account_id) === String(accountIdFromUrl)
    );
    if (match && (!selectedAccount || selectedAccount.accountId !== match.accountId)) {
      setSelectedAccount(match);
    }
  }, [accountIdFromUrl, completedAccounts, selectedAccount]);

  const clientId = selectedAccount?.clientId || user?.client_id;
  const awsAccountId = selectedAccount?.accountId;

  const setAccountInUrl = (account) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (account?.accountId) next.set("accountId", account.accountId);
      else next.delete("accountId");
      return next;
    });
  };

  const hydrateFromInventory = useCallback(async () => {
    if (!clientId || !awsAccountId) return;
    setStatusLoading(true);
    setTagsError(null);
    try {
      const listRes = await applicationDiscoveryService.listApplications(
        clientId,
        awsAccountId
      );
      setApplications(listRes.applications || []);
    } catch (error) {
      console.error("[hydrateFromInventory]", error);
      message.error(error.message || "Failed to load applications");
    } finally {
      setStatusLoading(false);
    }
  }, [clientId, awsAccountId]);

  useEffect(() => {
    setSelectedUntaggedKeys([]);
    setAddAttachKeys([]);
  }, [awsAccountId]);

  const loadUntaggedResources = useCallback(async () => {
    if (!clientId || !awsAccountId) return;
    setUntaggedLoading(true);
    setUntaggedError("");
    setUntaggedWarning("");
    try {
      const res = await applicationDiscoveryService.listUntaggedResources(
        clientId,
        awsAccountId,
        ["us-east-1"]
      );
      setUntaggedResources(res.items || []);
      const warns = res.warnings || [];
      if (warns.length) setUntaggedWarning(warns[0]);
    } catch (error) {
      console.error("[loadUntaggedResources]", error);
      setUntaggedResources([]);
      setUntaggedError(
        error.message ||
          "Failed to load untagged resources. Ensure Resource Explorer is enabled and the IAM role allows Search/GetView."
      );
    } finally {
      setUntaggedLoading(false);
    }
  }, [clientId, awsAccountId]);

  const handleDiscover = useCallback(async () => {
    if (!clientId || !awsAccountId) {
      message.warning("Select an AWS account first");
      return;
    }
    setDiscoveryLoading(true);
    setTagsError(null);
    setDiscoverWarning("");
    try {
      const result = await applicationDiscoveryService.discoverApplications(
        clientId,
        awsAccountId,
        { persist: true }
      );
      setApplications(result.applications || []);
      if (result.warnings?.length) {
        setDiscoverWarning(result.warnings.join(" "));
      }
      // Re-list so manual apps are preserved alongside discovered ones
      await hydrateFromInventory();
      message.success(
        `Discovered ${result.count ?? (result.applications || []).length} application(s)`
      );
    } catch (error) {
      const tagsErr = parseTagsUnreadableError(error);
      if (tagsErr) {
        setTagsError(tagsErr);
        message.error(tagsErr.message);
      } else {
        console.error("[handleDiscover]", error);
        message.error(error.message || "Discovery failed");
      }
    } finally {
      setDiscoveryLoading(false);
    }
  }, [clientId, awsAccountId, hydrateFromInventory]);

  useEffect(() => {
    if (!clientId || !awsAccountId) {
      setApplications([]);
      setUntaggedResources([]);
      setUntaggedError("");
      return;
    }
    let cancelled = false;
    (async () => {
      await hydrateFromInventory();
      if (!cancelled) await handleDiscover();
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId, awsAccountId, hydrateFromInventory, handleDiscover]);

  useEffect(() => {
    if (activeTab === TAB_KEY_UNTAGGED && clientId && awsAccountId) {
      loadUntaggedResources();
    }
  }, [activeTab, clientId, awsAccountId, loadUntaggedResources]);

  const filteredApps = useMemo(() => {
    let list = applications;
    if (activeTab !== TAB_KEY_UNTAGGED) {
      list = filterApplicationsByTab(list, activeTab);
      list = filterApplicationsBySearch(list, searchQuery);
    }
    return list;
  }, [applications, activeTab, searchQuery]);

  const handleAddApplication = async (values) => {
    if (!clientId || !awsAccountId) {
      message.warning("Select an AWS account first");
      return;
    }
    setAddSaving(true);
    try {
      if (editingApp?.app_slug) {
        await applicationDiscoveryService.updateApplication(
          clientId,
          awsAccountId,
          editingApp.app_slug,
          {
            name: values.name,
            description: values.description,
            owner: values.owner,
            type: values.type,
            environment: values.environment,
          }
        );
        message.success(`Updated ${values.name}`);
        setAddModalOpen(false);
        setEditingApp(null);
        setAddAttachKeys([]);
        addForm.resetFields();
        await hydrateFromInventory();
      } else {
        const attachItems = untaggedResources.filter((r) =>
          addAttachKeys.includes(r.arn)
        );
        let slug;
        if (attachItems.length) {
          const res = await applicationDiscoveryService.assignResources(
            clientId,
            awsAccountId,
            {
              createApplication: {
                name: values.name,
                description: values.description,
                owner: values.owner,
                type: values.type,
                environment: values.environment,
              },
              resources: attachItems.map((r) => ({
                arn: r.arn,
                region: r.region,
                resource_type: r.resource_type,
              })),
              discoverAfter: true,
              regions: ["us-east-1"],
            }
          );
          const taggedN = (res.tagged || []).length;
          const failedN = (res.failed || []).length;
          const failDetail = (res.failed || [])
            .slice(0, 2)
            .map(
              (f) =>
                f.error_message ||
                f.error_code ||
                (f.arn ? String(f.arn).split(":").slice(-1)[0] : "unknown")
            )
            .filter(Boolean)
            .join("; ");
          if (failedN && taggedN) {
            message.warning(
              `Added ${values.name}: tagged ${taggedN}, failed ${failedN}${
                failDetail ? `: ${failDetail}` : ""
              }`
            );
          } else if (failedN && !taggedN) {
            message.error(
              `Application created but tagging failed for ${failedN} resource(s)${
                failDetail ? `: ${failDetail}` : ""
              }`
            );
          } else {
            message.success(
              `Added ${values.name} and tagged ${taggedN} resource(s)`
            );
          }
          slug = res.app_slug || res.application?.app_slug;
        } else {
          const res = await applicationDiscoveryService.registerApplication(
            clientId,
            awsAccountId,
            {
              name: values.name,
              description: values.description,
              owner: values.owner,
              type: values.type,
              environment: values.environment,
            }
          );
          message.success(`Added ${res.application?.name || values.name}`);
          slug = res.application?.app_slug;
        }
        setAddModalOpen(false);
        setAddAttachKeys([]);
        addForm.resetFields();
        await hydrateFromInventory();
        if (attachItems.length) {
          await loadUntaggedResources();
        }
        if (slug) {
          navigate(
            `/applications/${encodeURIComponent(slug)}?accountId=${encodeURIComponent(awsAccountId)}`
          );
        }
      }
    } catch (error) {
      message.error(
        error.message ||
          (editingApp ? "Failed to update application" : "Failed to add application")
      );
    } finally {
      setAddSaving(false);
    }
  };

  const openAddApplication = async () => {
    setEditingApp(null);
    setAddAttachKeys([]);
    addForm.resetFields();
    addForm.setFieldsValue({
      type: "Web App",
      environment: "PROD",
    });
    setAddModalOpen(true);
    if (clientId && awsAccountId && !untaggedResources.length && !untaggedLoading) {
      setAddAttachLoading(true);
      try {
        await loadUntaggedResources();
      } finally {
        setAddAttachLoading(false);
      }
    }
  };

  const handleAssignUntagged = async () => {
    if (!clientId || !awsAccountId || !assignUntaggedSlug) {
      message.warning("Select a target application");
      return;
    }
    const items = untaggedResources.filter((r) =>
      selectedUntaggedKeys.includes(r.arn)
    );
    if (!items.length) {
      message.warning("Select at least one untagged resource");
      return;
    }
    setAssignUntaggedLoading(true);
    try {
      const res = await applicationDiscoveryService.assignResources(
        clientId,
        awsAccountId,
        {
          appSlug: assignUntaggedSlug,
          resources: items.map((r) => ({
            arn: r.arn,
            region: r.region,
            resource_type: r.resource_type,
          })),
          discoverAfter: true,
          regions: ["us-east-1"],
        }
      );
      const taggedN = (res.tagged || []).length;
      const failedN = (res.failed || []).length;
      const failDetail = (res.failed || [])
        .slice(0, 2)
        .map(
          (f) =>
            f.error_message ||
            f.error_code ||
            (f.arn ? String(f.arn).split(":").slice(-1)[0] : "unknown")
        )
        .filter(Boolean)
        .join("; ");
      if (failedN && taggedN) {
        message.warning(
          `Tagged ${taggedN}, failed ${failedN}${failDetail ? `: ${failDetail}` : ""}`
        );
      } else if (failedN && !taggedN) {
        message.error(
          `Failed to tag ${failedN} resource(s)${failDetail ? `: ${failDetail}` : ""}`
        );
      } else {
        message.success(`Tagged ${taggedN} resource(s) for ${assignUntaggedSlug}`);
      }
      setAssignUntaggedOpen(false);
      setAssignUntaggedSlug(null);
      setSelectedUntaggedKeys([]);
      await hydrateFromInventory();
      await loadUntaggedResources();
    } catch (error) {
      message.error(error.message || "Failed to assign resources");
    } finally {
      setAssignUntaggedLoading(false);
    }
  };

  const openEditApplication = (app) => {
    setEditingApp(app);
    addForm.setFieldsValue({
      name: appDisplayName(app),
      description: app.description || "",
      owner: app.owner || "",
      type: app.type || "Web App",
      environment: app.environment || "PROD",
    });
    setAddModalOpen(true);
  };

  const handleDeleteApplication = async (app) => {
    if (!clientId || !awsAccountId || !app?.app_slug) return;
    setDeleteLoading(true);
    try {
      await applicationDiscoveryService.deleteApplication(
        clientId,
        awsAccountId,
        app.app_slug
      );
      message.success(`Deleted ${appDisplayName(app)}`);
      await hydrateFromInventory();
    } catch (error) {
      message.error(error.message || "Failed to delete application");
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDetail = (app) => {
    if (!app?.app_slug || !awsAccountId) return;
    navigate(
      `/applications/${encodeURIComponent(app.app_slug)}?accountId=${encodeURIComponent(awsAccountId)}`
    );
  };

  const showAppsTable =
    activeTab === TAB_KEY_ALL ||
    activeTab === TAB_KEY_ONBOARDED ||
    activeTab === TAB_KEY_DISCOVERY;

  const appColumns = [
    {
      title: "App slug",
      dataIndex: "app_slug",
      key: "slug",
      width: 140,
      render: (slug) => <Text code style={{ fontSize: 12 }}>{slug}</Text>,
    },
    {
      title: "Name",
      key: "name",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Button type="link" style={{ padding: 0, height: "auto" }} onClick={() => openDetail(record)}>
            <Text strong>{appDisplayName(record)}</Text>
          </Button>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.description || getResourceSummary(record) || "—"}
          </Text>
        </Space>
      ),
    },
    {
      title: "Owner",
      key: "owner",
      width: 120,
      render: (_, record) =>
        record.owner ? (
          <Text>{record.owner}</Text>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Type",
      key: "type",
      width: 120,
      render: (_, record) => {
        if (record.type) {
          return <Tag color="purple">{record.type}</Tag>;
        }
        const type = getDominantServiceType(record);
        if (!type) return <Text type="secondary">—</Text>;
        return (
          <Tag color={serviceTypeTagColor(type)}>{formatServiceKey(type)}</Tag>
        );
      },
    },
    {
      title: "Coverage",
      key: "coverage",
      width: 100,
      render: (_, record) => {
        const { status } = getApplicationOnboardingState(record);
        return (
          <Tag color={coverageTagColor(status)}>{coverageDisplayLabel(status)}</Tag>
        );
      },
    },
    {
      title: "Logs",
      key: "logs",
      width: 150,
      render: (_, record) => {
        const state = getApplicationOnboardingState(record);
        return (
          <CoverageProgress
            icon={FileTextOutlined}
            iconColor="#3b82f6"
            onboarded={state.logsOnboarded}
            total={state.logsTotal}
            activeColor="#3b82f6"
            isDark={isDark}
            emptyLabel="No logs"
            kind="logs"
          />
        );
      },
    },
    {
      title: "Metrics",
      key: "metrics",
      width: 150,
      render: (_, record) => {
        const state = getApplicationOnboardingState(record);
        return (
          <CoverageProgress
            icon={LineChartOutlined}
            iconColor="#8b5cf6"
            onboarded={state.metricsOnboarded}
            total={state.metricsTotal}
            activeColor="#8b5cf6"
            isDark={isDark}
            emptyLabel="No metrics"
            kind="metrics"
          />
        );
      },
    },
    {
      title: "Regions",
      key: "regions",
      width: 140,
      render: (_, record) =>
        (record.regions || []).length ? (
          (record.regions || []).slice(0, 2).map((r) => (
            <Tag key={r} style={{ marginBottom: 2 }}>
              {r}
            </Tag>
          ))
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Edit">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              disabled={isViewer}
              onClick={(e) => {
                e.stopPropagation();
                openEditApplication(record);
              }}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this application?"
            description="Removes it from the inventory. Tagged AWS resources may reappear on Discover."
            okText="Delete"
            okButtonProps={{ danger: true, loading: deleteLoading }}
            disabled={isViewer}
            onConfirm={(e) => {
              e?.stopPropagation?.();
              return handleDeleteApplication(record);
            }}
            onCancel={(e) => e?.stopPropagation?.()}
          >
            <Tooltip title="Delete">
              <Button
                type="text"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={isViewer}
                onClick={(e) => e.stopPropagation()}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const untaggedColumns = [
    {
      title: "Service",
      dataIndex: "service_key",
      width: 120,
      render: (s) => (
        <Tag color={serviceTypeTagColor(s)}>{formatServiceKey(s) || s || "—"}</Tag>
      ),
    },
    {
      title: "Name",
      dataIndex: "name",
      width: 180,
      ellipsis: true,
      render: (n, r) => <Text strong>{n || r.arn || "—"}</Text>,
    },
    {
      title: "Type",
      dataIndex: "resource_type",
      width: 160,
      ellipsis: true,
      render: (t) => t || "—",
    },
    {
      title: "Region",
      dataIndex: "region",
      width: 110,
      render: (r) => r || "—",
    },
    {
      title: "ARN",
      dataIndex: "arn",
      ellipsis: true,
      render: (arn) => (
        <Text
          code
          style={{ fontSize: 11, wordBreak: "break-all" }}
          copyable={arn ? { text: arn } : false}
        >
          {arn || "—"}
        </Text>
      ),
    },
    {
      title: "Missing tag",
      dataIndex: "missing_tag_key",
      width: 140,
      render: (k) => <Tag>{k || "ApplicationName"}</Tag>,
    },
  ];

  return (
    <div
      style={{
        width: "100%",
        maxWidth: 1400,
        margin: "0 auto",
        padding: "8px 14px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 12,
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <Title level={4} style={{ margin: 0, flex: 1, minWidth: 160 }}>
          <AppstoreOutlined style={{ marginRight: 8 }} />
          Applications
        </Title>
        <Select
          showSearch
          placeholder="Select AWS account"
          style={{ minWidth: 260 }}
          loading={accountsLoading}
          value={selectedAccount?.accountId}
          optionFilterProp="children"
          onChange={(id) => {
            const account = completedAccounts.find((a) => a.accountId === id);
            setSelectedAccount(account || null);
            setAccountInUrl(account);
          }}
        >
          {completedAccounts.map((a) => (
            <Option key={a.accountId} value={a.accountId}>
              {a.aws_account_name || a.accountId} ({a.accountId})
            </Option>
          ))}
        </Select>
        <Tooltip title="Sync tagged AWS resources into the inventory">
          <Button
            icon={<ReloadOutlined />}
            loading={discoveryLoading}
            onClick={handleDiscover}
            disabled={!selectedAccount}
          >
            Discover
          </Button>
        </Tooltip>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={!selectedAccount || isViewer}
          onClick={openAddApplication}
        >
          Add Application
        </Button>
      </div>

      {tagsError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="Tags unreadable"
          description={
            <span>
              {tagsError.message}
              {tagsError.action ? (
                <>
                  {" "}
                  Required action: <Text code>{tagsError.action}</Text>
                </>
              ) : null}
              {tagsError.region ? <> in {tagsError.region}</> : null}.
            </span>
          }
        />
      )}

      {discoverWarning && (
        <Alert
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 12 }}
          message={discoverWarning}
          onClose={() => setDiscoverWarning("")}
        />
      )}

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
          flexWrap: "wrap",
        }}
      >
        <Tabs
          size="small"
          activeKey={activeTab}
          onChange={setActiveTab}
          style={{ flex: 1, minWidth: 280 }}
          items={[
            { key: TAB_KEY_ALL, label: "All" },
            { key: TAB_KEY_ONBOARDED, label: "Monitoring" },
            { key: TAB_KEY_DISCOVERY, label: "Ready to monitor" },
            {
              key: TAB_KEY_UNTAGGED,
              label: `Untagged${
                untaggedResources.length ? ` (${untaggedResources.length})` : ""
              }`,
            },
          ]}
        />
        {showAppsTable && (
          <Input.Search
            allowClear
            placeholder="Search applications..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 240 }}
          />
        )}
      </div>

      {showAppsTable && (
        <Table
          size="small"
          rowKey={(r) => r.app_slug}
          columns={appColumns}
          dataSource={filteredApps}
          loading={statusLoading || discoveryLoading}
          pagination={{ pageSize: 15, showSizeChanger: true }}
          scroll={{ x: 1200 }}
          onRow={(record) => ({
            onClick: (e) => {
              if (e.target.closest("button, a, .ant-checkbox-wrapper")) return;
              openDetail(record);
            },
            style: { cursor: "pointer" },
          })}
          locale={{
            emptyText: selectedAccount
              ? "No applications yet. Use Add Application, or Discover to sync tagged AWS resources."
              : "Select an AWS account to begin.",
          }}
        />
      )}

      {activeTab === TAB_KEY_UNTAGGED && (
        <div>
          {untaggedError ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Untagged resources unavailable"
              description={untaggedError}
              action={
                <Button size="small" onClick={loadUntaggedResources}>
                  Retry
                </Button>
              }
            />
          ) : null}
          {untaggedWarning ? (
            <Alert
              type="info"
              showIcon
              closable
              style={{ marginBottom: 12 }}
              message={untaggedWarning}
              onClose={() => setUntaggedWarning("")}
            />
          ) : null}
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              justifyContent: "space-between",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            <Space>
              <Button
                type="primary"
                disabled={isViewer || !selectedUntaggedKeys.length}
                onClick={() => {
                  setAssignUntaggedSlug(null);
                  setAssignUntaggedOpen(true);
                }}
              >
                Assign to application
                {selectedUntaggedKeys.length
                  ? ` (${selectedUntaggedKeys.length})`
                  : ""}
              </Button>
              {selectedUntaggedKeys.length ? (
                <Button size="small" onClick={() => setSelectedUntaggedKeys([])}>
                  Clear selection
                </Button>
              ) : null}
            </Space>
            <Button
              size="small"
              icon={<ReloadOutlined />}
              loading={untaggedLoading}
              onClick={loadUntaggedResources}
            >
              Refresh
            </Button>
          </div>
          <Table
            size="small"
            rowKey={(r) => r.arn || JSON.stringify(r)}
            rowSelection={
              isViewer
                ? undefined
                : {
                    selectedRowKeys: selectedUntaggedKeys,
                    onChange: (keys) => setSelectedUntaggedKeys(keys),
                  }
            }
            columns={untaggedColumns}
            dataSource={untaggedResources}
            loading={untaggedLoading}
            pagination={{ pageSize: 15 }}
            scroll={{ x: 1100 }}
            locale={{
              emptyText: untaggedError
                ? " "
                : "No untagged taggable resources found (or Resource Explorer index is empty)",
            }}
          />
        </div>
      )}

      <Modal
        title={editingApp ? "Edit Application" : "Add Application"}
        open={addModalOpen}
        onCancel={() => {
          setAddModalOpen(false);
          setEditingApp(null);
          setAddAttachKeys([]);
          addForm.resetFields();
        }}
        footer={null}
        destroyOnClose
        width={editingApp ? 520 : 640}
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
          {editingApp
            ? "Update application details in the platform inventory."
            : "Register a new application. Optionally attach untagged AWS resources (tags ApplicationName in the customer account)."}
        </Text>
        <Form
          form={addForm}
          layout="vertical"
          onFinish={handleAddApplication}
          initialValues={{ type: "Web App", environment: "PROD" }}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: "Name is required" }]}
          >
            <Input placeholder="e.g. Customer Portal" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={3} placeholder="What does this application do?" />
          </Form.Item>
          <Form.Item
            name="owner"
            label="Owner"
            rules={[{ required: true, message: "Owner is required" }]}
          >
            <Input placeholder="e.g. Emily Chen" />
          </Form.Item>
          <Form.Item name="type" label="Type">
            <Select options={APP_TYPE_OPTIONS.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="environment" label="Environment">
            <Select options={ENV_OPTIONS.map((e) => ({ value: e, label: e }))} />
          </Form.Item>
          {!editingApp ? (
            <Form.Item
              label="Attach untagged resources (optional)"
              extra="Writes ApplicationName on selected resources, then runs Discover."
            >
              <Select
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={
                  addAttachLoading || untaggedLoading
                    ? "Loading untagged resources…"
                    : untaggedResources.length
                      ? "Select resources to tag"
                      : "No untagged resources loaded"
                }
                loading={addAttachLoading || untaggedLoading}
                value={addAttachKeys}
                onChange={setAddAttachKeys}
                disabled={isViewer}
                options={untaggedResources.map((r) => ({
                  value: r.arn,
                  label: `${r.name || r.arn} (${r.service_key || r.resource_type || "resource"} · ${r.region || "—"})`,
                }))}
                maxTagCount="responsive"
              />
            </Form.Item>
          ) : null}
          <Form.Item style={{ marginBottom: 0, textAlign: "right" }}>
            <Space>
              <Button
                onClick={() => {
                  setAddModalOpen(false);
                  setEditingApp(null);
                  setAddAttachKeys([]);
                  addForm.resetFields();
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit" loading={addSaving}>
                {editingApp
                  ? "Save changes"
                  : addAttachKeys.length
                    ? `Add & tag (${addAttachKeys.length})`
                    : "Add Application"}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Assign to application"
        open={assignUntaggedOpen}
        onCancel={() => {
          setAssignUntaggedOpen(false);
          setAssignUntaggedSlug(null);
        }}
        onOk={handleAssignUntagged}
        confirmLoading={assignUntaggedLoading}
        okText="Tag in AWS"
        okButtonProps={{ disabled: isViewer || !assignUntaggedSlug }}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Text type="secondary">
            Tag {selectedUntaggedKeys.length} selected resource(s) with
            ApplicationName for an existing application, then refresh Discover.
          </Text>
          <Text type="secondary">Target application</Text>
          <Select
            showSearch
            placeholder="Select application"
            style={{ width: "100%" }}
            value={assignUntaggedSlug}
            onChange={setAssignUntaggedSlug}
            optionFilterProp="children"
          >
            {applications.map((a) => (
              <Option key={a.app_slug} value={a.app_slug}>
                {appDisplayName(a)} ({a.app_slug})
              </Option>
            ))}
          </Select>
          {!applications.length ? (
            <Alert
              type="info"
              showIcon
              message="No applications yet — use Add Application to create one and attach resources there."
            />
          ) : null}
        </Space>
      </Modal>
    </div>
  );
};

export default ApplicationsPage;
